import { PlusCircleIcon } from "@heroicons/react/outline";
import { IAttractionNoPhaseTicketForm, ISeatmapTicketsForm, ISimpleEventDetail, ITicketItem, ITicketsResponseData, ProductType } from "interfaces";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { NoParentTicketForm } from "../forms/noParentTicketForm";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { SubTickets } from "./subTickets";
import { useCreate, useOne, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { HistoryLodModal } from "components/historyLogModal";
import { FullScreenLoading } from "components/fullScreenLoading";

interface IndividualTicketField extends ITicketItem {
  id: string;
}

const INITIAL_TICKET_MODAL_CONFIG = {
  open: false,
}

export const Tickets = ({eventDetailData, fetchEventDetail} : {eventDetailData: ISimpleEventDetail, fetchEventDetail: () => void}) => {
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);
  const [ticketModalConfig, setTicketModalConfig] = useState(INITIAL_TICKET_MODAL_CONFIG)

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [isDragged, setIsDragged] = useState(false);

  const { mutate, isLoading: isCreating } = useCreate();
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdate();

  const {
    control,
    watch,
  } = useFormContext<ISeatmapTicketsForm>();

  const { refetch: fetchTickets, data: ticketResponseData } =
  useOne<ITicketsResponseData>({
    resource: "content/events",
    id: `${eventDetailData.id}/tickets`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
  });

  const ticketsData = ticketResponseData?.data ?? null;

  const { fields, move, replace } = useFieldArray({
    control,
    name: "tickets"
  });

  useEffect(() => {
    if (ticketsData) {
      replace(ticketsData.tickets);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsData])

  useEffect(() => {
    if (isDragged && ticketsData) {
      replace(ticketsData.tickets);
      setIsDragged(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragged])

  useEffect(() => {
    if (draggedItemId) {
      _moveField(fields.map(field => field.ticket?.id ?? ""), draggedItemId);
      setDraggedItemId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedItemId])

  const _createNoPhaseTicket = (submitValues: IAttractionNoPhaseTicketForm) => {
    mutate(
      {
        resource: "content/tickets/attractions/single",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventDetailData.id,
          tickets: [submitValues]
        },
      },
      {
        onSuccess: (data) => {
          fetchTickets();
          fetchEventDetail();
          toast.success("Create ticket successfully");
          _onCloseModal();
        },
      }
    );
  }

  const _onCloseModal = () => {
    setTicketModalConfig(INITIAL_TICKET_MODAL_CONFIG);
  }

  const handleDrag = ({ source, destination }: any) => {
    if (destination && (source.index !== destination.index)) {
      move(source.index, destination.index);
      setDraggedItemId(fields[source.index].ticket?.id ?? null);
    }
  };

  const _moveField = (ticketOrder: string[], movedTicketId: string) => {
    updateMutation(
      {
        resource: "content/tickets",
        id: "reorder",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          event_id: eventDetailData.id,
          ticket_order: ticketOrder,
          moved_ticket: movedTicketId
        },
      },
      {
        onSuccess: (data) => {
          toast.success("Move ticket successfully");
        },
        onSettled: async () => {
          await fetchTickets();
          setIsDragged(true);
        }
      }
    );
  }

  return (
    <div className="box-container">
      <div>
        <DragDropContext onDragEnd={handleDrag}>
          <div className="grid sm:grid-cols-6 lg:gap-x-6">
            <Droppable droppableId="ticket-items" isDropDisabled={isUpdating}>
              {(provided, snapshot) => (
                <div
                  className="col-span-full"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {fields.map((field: IndividualTicketField, index: number) => (
                    <Draggable
                      key={`ticket[${index}]`}
                      draggableId={`item-${index}`}
                      isDragDisabled={isEmpty(field.ticket)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <SubTickets 
                          eventDetailData={eventDetailData} 
                          ticketIndex={index} 
                          key={field.id} 
                          parentTicket={false}
                          _moveField={_moveField}
                          setOpenLogModalId={setOpenLogModalId}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
        <div className="text-center text-sm">
          {isEmpty(fields) ? <p className="text-center text-sm">No Tickets</p> : <></>}
        </div>
        <div className="w-full text-center">
          <button
            type="button"
            className="flex flex-row items-center mx-auto w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
            onClick={() => setTicketModalConfig({
              open: true
            })}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
            <span className="shrink-0 text-sm font-medium">Add more ticket</span>
          </button>
        </div>
      </div>
      <NoParentTicketForm 
        productType={ProductType.TICKET}
        open={ticketModalConfig.open}
        onClose={_onCloseModal}
        eventDetailData={eventDetailData}
        defaultValues={null}
        onSubmit={(data) => _createNoPhaseTicket(data)}
      />
      <HistoryLodModal
        resource={`content/tickets/${openLogModalId}/history`}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="ticket"
        id="ticket"
        customFilters={watch("tickets").find(ticket => ticket.ticket?.id === openLogModalId) ? [
          {field: "parent_ticket", operator: "eq", value: true}
        ] : []}
      />
      {(isCreating || isUpdating) ? <FullScreenLoading /> : <></>}
    </div>
  )
}